/**
 * Main-thread client for `pipeline.worker.ts`. Exposes `renderInWorker` with
 * the same shape as `renderToTrustedHast` so callers swap one import.
 *
 * Behavior:
 *  - One shared Worker instance for the page (markdown rendering is naturally
 *    serialized — there's a single active document — and a pool would only
 *    multiply memory for marginal gain).
 *  - Each call gets a monotonic id; results are routed back by id. Consumers
 *    handle their own staleness (see RenderedPreview's `cancelled` flag); the
 *    client doesn't reject superseded requests.
 *  - Lazy worker construction: the worker isn't spawned until the first call,
 *    so opening the app costs nothing if the user never touches the preview.
 *  - In-thread fallback for environments without a usable Worker (SSR, test
 *    runners — jsdom Worker doesn't execute module workers). Falls back
 *    transparently: callers can't tell which path ran.
 */
import { renderToTrustedHast, applyKatex } from '@slate/core';
import type { Root as HastRoot } from 'hast';
import type { PipelineRequest, PipelineResponse } from './pipeline.worker';

/**
 * Worker is force-disabled under Vitest (`MODE === 'test'`) because jsdom's
 * Worker shim accepts `new Worker(...)` but never executes the module — calls
 * would hang. The in-thread fallback keeps the existing test suite working.
 */
const WORKER_ENABLED: boolean = (() => {
  try {
    const env = (import.meta as unknown as { env?: { MODE?: string } }).env;
    if (env?.MODE === 'test') return false;
  } catch {
    /* import.meta.env not available — fall through to runtime check */
  }
  return typeof Worker !== 'undefined';
})();

let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<
  number,
  { resolve: (hast: HastRoot) => void; reject: (err: Error) => void }
>();

function ensureWorker(): Worker | null {
  if (worker || !WORKER_ENABLED) return worker;
  try {
    const w = new Worker(new URL('./pipeline.worker.ts', import.meta.url), {
      type: 'module',
      name: 'slate-pipeline',
    });
    w.addEventListener('message', (e: MessageEvent<PipelineResponse>) => {
      const slot = pending.get(e.data.id);
      if (!slot) return;
      pending.delete(e.data.id);
      if (e.data.ok) {
        // KaTeX runs on the main thread (it touches `document` at module-init,
        // which crashes in a Worker). Apply it here to finalize the tree.
        try {
          slot.resolve(applyKatex(e.data.hast));
        } catch (err) {
          slot.reject(err instanceof Error ? err : new Error(String(err)));
        }
      } else slot.reject(new Error(e.data.error));
    });
    w.addEventListener('error', (e) => {
      // Fatal worker error — fail every pending request and reset so the next
      // call recreates a fresh worker (or falls back in-thread).
      const err = new Error(e.message || 'pipeline worker error');
      for (const slot of pending.values()) slot.reject(err);
      pending.clear();
      worker = null;
    });
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
}

/**
 * Render markdown through the trusted pipeline. Off main thread when a Worker
 * is available; in-thread fallback otherwise. Same return shape as
 * `renderToTrustedHast` so callers swap one import.
 */
export function renderInWorker(
  markdown: string,
  opts: { dark?: boolean } = {},
): Promise<HastRoot> {
  const w = ensureWorker();
  if (!w) return renderToTrustedHast(markdown, opts);
  return new Promise<HastRoot>((resolve, reject) => {
    const id = ++nextId;
    pending.set(id, { resolve, reject });
    const req: PipelineRequest = { id, markdown, opts };
    w.postMessage(req);
  });
}

/** Whether `renderInWorker` will run off the main thread (vs. fall back). */
export const workerEnabled = WORKER_ENABLED;

/** Test-only: tear down the shared worker and pending requests. */
export function _resetForTests(): void {
  for (const slot of pending.values()) slot.reject(new Error('reset'));
  pending.clear();
  if (worker) {
    worker.terminate();
    worker = null;
  }
  nextId = 0;
}
